import { useState } from "react";
import type { Finding } from "@/lib/qa";
import { RULE_EXPLANATIONS } from "@/lib/qa";
import { strings } from "@/lib/strings";
import { cn } from "@/lib/utils";

/**
 * QA findings shown beside the field they concern (docs/08 §8.3).
 *
 * Each carries a "Why?" link explaining the rule, because a volunteer who
 * understands why a warning fired makes fewer of them.
 */
export function FindingList({
  findings,
  id,
  className,
}: {
  findings: Finding[];
  id?: string;
  className?: string;
}) {
  if (findings.length === 0) return null;

  return (
    <ul id={id} className={cn("space-y-1", className)}>
      {findings.map((finding) => (
        <FindingRow key={finding.code} finding={finding} />
      ))}
    </ul>
  );
}

function FindingRow({ finding }: { finding: Finding }) {
  const [showWhy, setShowWhy] = useState(false);
  const explanation = RULE_EXPLANATIONS[finding.code];

  const tone =
    finding.severity === "blocking"
      ? "text-blocking"
      : finding.severity === "warning"
        ? "text-warning"
        : "text-info";

  return (
    <li className={cn("text-xs", tone)}>
      <span className="font-medium">
        {finding.severity === "blocking" ? strings.qa.blocking : strings.qa.warning}:
      </span>{" "}
      {finding.message}
      {explanation && (
        <>
          {" "}
          <button
            type="button"
            onClick={() => setShowWhy((open) => !open)}
            className="underline"
            aria-expanded={showWhy}
          >
            {strings.entry.whyThisRule}
          </button>
          {showWhy && (
            <p className="mt-1 rounded bg-slate-50 p-2 text-slate-600">{explanation}</p>
          )}
        </>
      )}
    </li>
  );
}
